import React from 'react';
import { ToggleSwitch } from '@americanexpress/dls-react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import colors from 'src/styles/colors.module.scss';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';
import styles from './ruleSetting.module.scss';
import { useDeleteUserAttribute, useUpdateUserAttributes, useUserAttributes } from 'src/hooks/queries/users';
import { UserAttributes } from 'src/types/user-attributes.enum';

export default function RuleSettingWrapper(): JSX.Element {
    const { data: userAttributes } = useUserAttributes();
    const { mutateAsync: updateAttributes } = useUpdateUserAttributes();
    const { mutateAsync: deleteAttribute } = useDeleteUserAttribute();
    const neverShowCreateRulePrompt: boolean = userAttributes?.[UserAttributes.NEVER_SHOW_CREATE_RULE_PROMPT];

    const handleToggleClick = async (): Promise<void> => {
        if (neverShowCreateRulePrompt) {
            await deleteAttribute(UserAttributes.NEVER_SHOW_CREATE_RULE_PROMPT);
        } else {
            await updateAttributes({
                [UserAttributes.NEVER_SHOW_CREATE_RULE_PROMPT]: true
            });
        }
    };

    return <RuleSetting handleToggleClick={handleToggleClick} checked={!neverShowCreateRulePrompt} />;
}

interface RuleSettingProps {
    handleToggleClick: () => Promise<void>;
    checked: boolean;
}

export function RuleSetting({ handleToggleClick, checked }: RuleSettingProps): JSX.Element {
    return (
        <div className={styles.ruleSetting} data-cy={Selectors.Transactions.RULE_SETTING}>
            <Typography component="h2" variant={TypographyVariants.HEADING_S} className={colors.brandStrong}>
                {LiteralsService.getLiteral('RuleSetting.title')}
            </Typography>
            <div className={styles.content}>
                <div>
                    <Typography component="p" variant={TypographyVariants.BODY_BOLD}>
                        {LiteralsService.getLiteral('RuleSetting.askToCreateRule')}
                    </Typography>
                    <Typography component="p" variant={TypographyVariants.BODY} className={styles.description}>
                        {LiteralsService.getLiteral('RuleSetting.description')}
                    </Typography>
                </div>
                <ToggleSwitch
                    className={styles.toggleSwitch}
                    onClick={handleToggleClick}
                    aria-label={
                        checked
                            ? LiteralsService.getLiteral('RuleSetting.toggleOn')
                            : LiteralsService.getLiteral('RuleSetting.toggleOff')
                    }
                    checked={checked}
                    data-cy={Selectors.Transactions.RULE_SETTING_TOGGLE_BUTTON}
                />
            </div>
        </div>
    );
}
